import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Sparkles,
  User,
  Send,
  X,
  Minus,
  Maximize2,
  Minimize2,
  ExternalLink,
  Download,
  FileSpreadsheet,
  FileType,
  FileText,
  Loader2,
  Database,
  Move,
  MessageSquare,
  Search,
  Check,
  AlertCircle
} from 'lucide-react';
import { marked } from 'marked';
import { exportToCSV, exportToPDF, exportToMarkdown, hasMarkdownTable } from './chatExportUtils';
import { fetchAIStatusRecommendation, getAuthHeaders } from '../lib/db';
import { Lead, resolveStatusColor } from '../types';

export interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  toolsCalled?: string[];
  model?: string;
}

export interface LogisticsChatWidgetProps {
  leads?: Lead[];
  onUpdateStatus?: (leadId: string, newStatus: string) => void | Promise<void>;
  statusColors?: Record<string, string>;
}

type WindowMode = 'normal' | 'expanded' | 'maximized' | 'minimized';

const ICON_SIZE = 72;
const VIEWPORT_MARGIN = 16;
const ICON_STORAGE_KEY = 'ael_chatbot_icon_pos_v1';
const WINDOW_SIZE_STORAGE_KEY = 'ael_chatbot_window_size_v1';
const DEFAULT_CHAT_WIDTH = 900;
const DEFAULT_CHAT_HEIGHT = 680;
const MIN_CHAT_WIDTH = 420;
const MAX_CHAT_WIDTH = 1400;
const MIN_CHAT_HEIGHT = 420;
const MAX_CHAT_HEIGHT = 960;

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';

export const DEFAULT_HALO_MODELS: Array<{ id: string; name: string }> = [
  { id: 'gemini/gemini-4-argon', name: 'Gemini 4 Argon' },
  { id: 'gemini/gemini-3.8-flash', name: 'Gemini 3.8 Flash' },
  { id: 'agy/gemini-3.8-flash-tiered', name: 'Gemini 3.8 Flash Tiered (AGY)' },
  { id: 'agy/gemini-3.8-flash-high', name: 'Gemini 3.8 Flash High (AGY)' },
  { id: 'agy/gemini-3.8-flash-medium', name: 'Gemini 3.8 Flash Medium (AGY)' },
  { id: 'antigravity/gemini-3.8-flash-tiered', name: 'Gemini 3.8 Flash Tiered (IDE)' }
];

const SUGGESTED_PROMPTS = [
  'Which leads have overdue or upcoming follow-ups?',
  'Show pipeline summary by status and sales rep',
  'What is the status of pending online forms and DocuSeal invites?',
  'Summarize active Non-Floral shipments and closing board'
];

function clampIconPosition(x: number, y: number): { x: number; y: number } {
  if (typeof window === 'undefined') return { x, y };
  const maxX = Math.max(VIEWPORT_MARGIN, window.innerWidth - ICON_SIZE - VIEWPORT_MARGIN);
  const maxY = Math.max(VIEWPORT_MARGIN, window.innerHeight - ICON_SIZE - VIEWPORT_MARGIN);
  return {
    x: Math.max(VIEWPORT_MARGIN, Math.min(maxX, x)),
    y: Math.max(VIEWPORT_MARGIN, Math.min(maxY, y))
  };
}

function getInitialIconPosition(): { x: number; y: number } {
  if (typeof window === 'undefined') return { x: 24, y: 24 };
  try {
    const saved = localStorage.getItem(ICON_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
        return clampIconPosition(parsed.x, parsed.y);
      }
    }
  } catch {
    // ignore storage errors
  }
  return clampIconPosition(
    window.innerWidth - ICON_SIZE - 24,
    window.innerHeight - ICON_SIZE - 24
  );
}

export const LogisticsChatWidget: React.FC<LogisticsChatWidgetProps> = ({
  leads = [],
  onUpdateStatus,
  statusColors = {}
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'advisor'>('chat');
  const [windowMode, setWindowMode] = useState<WindowMode>('normal');
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(WINDOW_SIZE_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (typeof parsed?.width === 'number' && typeof parsed?.height === 'number') {
            return {
              width: Math.max(MIN_CHAT_WIDTH, Math.min(MAX_CHAT_WIDTH, parsed.width)),
              height: Math.max(MIN_CHAT_HEIGHT, Math.min(MAX_CHAT_HEIGHT, parsed.height))
            };
          }
        }
      } catch {
        // ignore storage errors
      }
    }
    return { width: DEFAULT_CHAT_WIDTH, height: DEFAULT_CHAT_HEIGHT };
  });
  const [iconPos, setIconPos] = useState<{ x: number; y: number }>(getInitialIconPosition);
  const [isDraggingIcon, setIsDraggingIcon] = useState<boolean>(false);

  // Tab 1: AI CRM Copilot state (matching Armellini AI Hub)
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'init-1',
      sender: 'assistant',
      text: "Hello! I am your **Armellini AI CRM Agent**, connected directly to the live database. I can query and aggregate across **Leads & Pipelines**, **Follow-Up Schedules**, **Online Forms & DocuSeal E-Signatures**, **Shippers / Consignees / Bill-To Accounts**, **Non-Floral Shipments**, **Service Quotes**, and **Tracked Gmail / Lara Emails**.",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeExportMenu, setActiveExportMenu] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>('gemini/gemini-4-argon');
  const [availableModels, setAvailableModels] = useState<Array<{ id: string; name: string }>>(DEFAULT_HALO_MODELS);

  // Tab 2: Pipeline Advisor state (matching Armellini AI Hub)
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingLeadId, setLoadingLeadId] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<Record<string, { recommendedStatus: string; reasoning: string }>>({});
  const [appliedLeads, setAppliedLeads] = useState<Record<string, boolean>>({});

  useEffect(() => {
    async function loadConfig() {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch('/api/ai/config', { headers });
        if (res.ok) {
          const cfg = await res.json();
          if (cfg.models && Array.isArray(cfg.models) && cfg.models.length > 0) {
            setAvailableModels(cfg.models);
          }
          if (cfg.defaultModel) {
            setSelectedModel(cfg.defaultModel);
          }
        }
      } catch (e) {
        console.warn('Failed to load AI config in widget:', e);
      }
    }
    loadConfig();
  }, []);

  // Keep icon inside viewport on window resize
  useEffect(() => {
    const handleWindowResize = () => {
      setIconPos(prev => clampIconPosition(prev.x, prev.y));
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);
  const resizeStartRef = useRef<{ x: number; y: number; w: number; h: number; dir: ResizeDirection }>({
    x: 0,
    y: 0,
    w: DEFAULT_CHAT_WIDTH,
    h: DEFAULT_CHAT_HEIGHT,
    dir: 'nw'
  });
  const iconDragRef = useRef({
    active: false,
    hasMoved: false,
    startX: 0,
    startY: 0,
    origX: 0,
    origY: 0
  });

  useEffect(() => {
    if (isOpen && activeTab === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, isOpen, activeTab]);

  // Handle Dragging the Chatbot Icon when closed (stationary when open)
  const handleIconPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    if (isOpen) {
      setIsOpen(false);
      return;
    }

    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    iconDragRef.current = {
      active: true,
      hasMoved: false,
      startX: e.clientX,
      startY: e.clientY,
      origX: iconPos.x,
      origY: iconPos.y
    };
  };

  const handleIconPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!iconDragRef.current.active || isOpen) return;
    const dx = e.clientX - iconDragRef.current.startX;
    const dy = e.clientY - iconDragRef.current.startY;

    if (!iconDragRef.current.hasMoved && Math.hypot(dx, dy) > 5) {
      iconDragRef.current.hasMoved = true;
      setIsDraggingIcon(true);
    }

    if (iconDragRef.current.hasMoved) {
      const next = clampIconPosition(
        iconDragRef.current.origX + dx,
        iconDragRef.current.origY + dy
      );
      setIconPos(next);
    }
  };

  const handleIconPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!iconDragRef.current.active) return;
    const wasDragged = iconDragRef.current.hasMoved;
    iconDragRef.current.active = false;
    setIsDraggingIcon(false);

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore if not captured
    }

    if (wasDragged) {
      try {
        localStorage.setItem(ICON_STORAGE_KEY, JSON.stringify(iconPos));
      } catch {
        // ignore storage errors
      }
    } else {
      // Clicked without dragging -> become stationary and open the chat window
      setWindowMode('normal');
      setIsOpen(true);
    }
  };

  // Handle Drag Resizing of Chat Window from corners and bottom/edges
  const updateAndPersistDimensions = useCallback((nextWidth: number, nextHeight: number) => {
    const maxW = typeof window !== 'undefined' ? Math.min(MAX_CHAT_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2) : MAX_CHAT_WIDTH;
    const maxH = typeof window !== 'undefined' ? Math.min(MAX_CHAT_HEIGHT, window.innerHeight - VIEWPORT_MARGIN * 2) : MAX_CHAT_HEIGHT;
    const clamped = {
      width: Math.round(Math.max(MIN_CHAT_WIDTH, Math.min(maxW, nextWidth))),
      height: Math.round(Math.max(MIN_CHAT_HEIGHT, Math.min(maxH, nextHeight)))
    };
    setDimensions(clamped);
    try {
      localStorage.setItem(WINDOW_SIZE_STORAGE_KEY, JSON.stringify(clamped));
    } catch {
      // ignore storage errors
    }
  }, []);

  const onResize = useCallback(
    (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const { x, y, w, h, dir } = resizeStartRef.current;
      const deltaX = e.clientX - x;
      const deltaY = e.clientY - y;

      let nextW = w;
      let nextH = h;

      if (dir.includes('w')) {
        nextW = w - deltaX;
      } else if (dir.includes('e')) {
        nextW = w + deltaX;
      }

      if (dir.includes('n')) {
        nextH = h - deltaY;
      } else if (dir.includes('s')) {
        nextH = h + deltaY;
      }

      updateAndPersistDimensions(nextW, nextH);
    },
    [updateAndPersistDimensions]
  );

  const stopResize = useCallback(() => {
    isResizingRef.current = false;
    document.removeEventListener('mousemove', onResize);
    document.removeEventListener('mouseup', stopResize);
  }, [onResize]);

  const startResize = (dir: ResizeDirection) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (windowMode === 'expanded') {
      setWindowMode('normal');
    }
    isResizingRef.current = true;
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      w: dimensions.width,
      h: dimensions.height,
      dir
    };
    document.addEventListener('mousemove', onResize);
    document.addEventListener('mouseup', stopResize);
  };

  const adjustWindowSize = (deltaW: number, deltaH: number) => {
    if (windowMode === 'expanded' || windowMode === 'maximized') {
      setWindowMode('normal');
    }
    updateAndPersistDimensions(dimensions.width + deltaW, dimensions.height + deltaH);
  };

  const resetWindowSize = () => {
    if (windowMode === 'expanded' || windowMode === 'maximized') {
      setWindowMode('normal');
    }
    updateAndPersistDimensions(DEFAULT_CHAT_WIDTH, DEFAULT_CHAT_HEIGHT);
  };

  // Compute anchored chat window coordinates relative to the stationary icon
  const getChatWindowPositionStyle = (): React.CSSProperties => {
    if (windowMode === 'maximized' || typeof window === 'undefined') {
      return {};
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (windowMode === 'expanded') {
      const w = Math.min(Math.max(dimensions.width, 1080), vw - VIEWPORT_MARGIN * 2);
      const h = Math.min(Math.max(dimensions.height, 760), vh - VIEWPORT_MARGIN * 2);
      return {
        left: Math.max(VIEWPORT_MARGIN, Math.round((vw - w) / 2)),
        top: Math.max(VIEWPORT_MARGIN, Math.round((vh - h) / 2)),
        width: w,
        height: h
      };
    }

    const w = Math.min(dimensions.width, vw - VIEWPORT_MARGIN * 2);
    const h = Math.min(dimensions.height, vh - VIEWPORT_MARGIN * 2);

    const isRightHalf = iconPos.x + ICON_SIZE / 2 >= vw / 2;
    let left = isRightHalf ? iconPos.x + ICON_SIZE - w : iconPos.x;
    left = Math.max(VIEWPORT_MARGIN, Math.min(vw - w - VIEWPORT_MARGIN, left));

    const spaceAbove = iconPos.y - VIEWPORT_MARGIN - 12;
    const spaceBelow = vh - (iconPos.y + ICON_SIZE + 12) - VIEWPORT_MARGIN;
    let top: number;
    if (spaceAbove >= h || spaceAbove >= spaceBelow) {
      top = Math.max(VIEWPORT_MARGIN, iconPos.y - h - 12);
    } else {
      top = Math.min(vh - h - VIEWPORT_MARGIN, iconPos.y + ICON_SIZE + 12);
    }

    return {
      left,
      top,
      width: w,
      height: h
    };
  };

  // Send Message to Express API -> Halo AI -> CRM Data Engine (same flow as Armellini AI Hub)
  const sendMessage = async (overrideText?: string) => {
    const textToSend = (overrideText ?? input).trim();
    if (!textToSend || loading) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    if (!overrideText) setInput('');
    setLoading(true);

    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: textToSend,
          history: messages.slice(1).map(m => ({ role: m.sender, content: m.text })), // omit initial welcome message from LLM prompt history
          model: selectedModel
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Server returned status ${res.status}`);
      }

      const data = await res.json();
      const replyContent = data.reply || data.response || 'No response details returned from assistant.';

      const aiMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'assistant',
        text: replyContent,
        timestamp: new Date(),
        toolsCalled: data.toolsCalled,
        model: data.model
      };

      setMessages(prev => [...prev, aiMsg]);
    } catch (err: any) {
      setMessages(prev => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          sender: 'assistant',
          text: `⚠️ Unable to process chat request (${err.message || 'Network error'}).`,
          timestamp: new Date()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleGetRecommendation = async (lead: Lead) => {
    setLoadingLeadId(lead.id);
    try {
      const data = await fetchAIStatusRecommendation(lead.nextSteps || '', lead.status);
      setRecommendations(prev => ({
        ...prev,
        [lead.id]: {
          recommendedStatus: data.recommendedStatus,
          reasoning: data.reasoning
        }
      }));
    } catch (err) {
      console.error(err);
      setRecommendations(prev => ({
        ...prev,
        [lead.id]: {
          recommendedStatus: lead.status,
          reasoning: lead.nextSteps
            ? `Local Analysis: Continue following up on: "${lead.nextSteps}".`
            : "Please enter some notes in 'Next Steps' first to generate an AI suggestion."
        }
      }));
    } finally {
      setLoadingLeadId(null);
    }
  };

  const handleApplyStatus = async (leadId: string, newStatus: string) => {
    if (!onUpdateStatus) return;
    try {
      await onUpdateStatus(leadId, newStatus);
      setAppliedLeads(prev => ({ ...prev, [leadId]: true }));
      setTimeout(() => {
        setRecommendations(prev => {
          const next = { ...prev };
          delete next[leadId];
          return next;
        });
        setAppliedLeads(prev => {
          const next = { ...prev };
          delete next[leadId];
          return next;
        });
      }, 1500);
    } catch (err) {
      console.error('Failed to update status via AI Advisor:', err);
    }
  };

  const getStatusBgColor = (status: string) => {
    return resolveStatusColor(status, statusColors);
  };

  const candidates = leads.filter(
    lead =>
      (lead.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (lead.company || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggleExpandWindow = () => {
    setWindowMode(prev => (prev === 'expanded' ? 'normal' : 'expanded'));
  };

  const renderMarkdown = (text: string) => {
    try {
      return { __html: marked.parse(text) as string };
    } catch {
      return { __html: text };
    }
  };

  return (
    <>
      {/* Floating Armellini Truck Chatbot Icon:
          - Movable/draggable on the page when closed (!isOpen)
          - Stationary when clicked (isOpen === true) */}
      {windowMode !== 'maximized' && windowMode !== 'expanded' && (
        <button
          type="button"
          onPointerDown={handleIconPointerDown}
          onPointerMove={handleIconPointerMove}
          onPointerUp={handleIconPointerUp}
          style={{
            left: iconPos.x,
            top: iconPos.y,
            width: ICON_SIZE,
            height: ICON_SIZE,
            touchAction: 'none'
          }}
          title={
            isOpen
              ? 'Chatbot stationary (Click to close chat window)'
              : 'Drag to move anywhere on page • Click to open Armellini AI Chatbot'
          }
          className={`fixed z-50 rounded-full p-1 bg-gradient-to-br from-sky-500 via-blue-700 to-slate-900 shadow-2xl border-2 transition-transform select-none group ${
            isOpen
              ? 'border-amber-400 ring-4 ring-sky-500/30 cursor-pointer scale-100'
              : isDraggingIcon
              ? 'border-sky-300 scale-110 cursor-grabbing shadow-sky-500/40'
              : 'border-white/90 dark:border-slate-700 hover:scale-105 cursor-grab hover:shadow-sky-500/30'
          }`}
        >
          <div className="w-full h-full rounded-full overflow-hidden bg-slate-900 relative">
            <img
              src="/chatbot-icon.jpg"
              alt="Armellini AI Chatbot"
              draggable={false}
              className="w-full h-full object-cover object-center pointer-events-none select-none"
            />
          </div>

          {/* Status indicator badge */}
          <span
            className={`absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white dark:border-slate-900 text-[10px] font-bold shadow ${
              isOpen ? 'bg-amber-500 text-slate-950' : 'bg-emerald-500 text-white'
            }`}
          >
            {isOpen ? <X className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
          </span>

          {/* Hover label when closed and not dragging */}
          {!isOpen && !isDraggingIcon && (
            <span className="pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-900/95 px-2.5 py-1 text-[11px] font-semibold text-white opacity-0 shadow-lg border border-slate-700 transition-opacity group-hover:opacity-100">
              AEL AI Assistant • Drag or Click
            </span>
          )}
        </button>
      )}

      {/* Minimized Pill Docked Next to Stationary Icon */}
      {isOpen && windowMode === 'minimized' && (
        <div
          style={{
            left: Math.max(VIEWPORT_MARGIN, Math.min(window.innerWidth - 320 - VIEWPORT_MARGIN, iconPos.x - 250)),
            top: Math.max(VIEWPORT_MARGIN, iconPos.y + 8)
          }}
          className="fixed z-50 flex items-center justify-between w-72 px-3.5 py-2.5 bg-slate-900 text-white rounded-xl shadow-2xl border border-slate-700"
        >
          <div className="flex items-center gap-2.5">
            <img
              src="/chatbot-icon.jpg"
              alt="AEL Assistant"
              className="w-7 h-7 rounded-full object-cover border border-sky-400"
            />
            <div>
              <div className="text-xs font-bold leading-tight">AEL AI Assistant</div>
              <div className="text-[10px] text-sky-400 font-mono truncate max-w-[150px]">{selectedModel}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setWindowMode('normal')}
              title="Restore chat window"
              className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-white cursor-pointer"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              title="Close and unlock icon"
              className="p-1.5 hover:bg-slate-800 rounded text-slate-300 hover:text-red-400 cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Backdrop when in expanded large-window mode */}
      {isOpen && windowMode === 'expanded' && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-xs"
          onClick={() => setWindowMode('normal')}
        />
      )}

      {/* Stationary Chat Window (same tabs & functionality as Armellini AI Hub) */}
      {isOpen && windowMode !== 'minimized' && (
        <div
          style={getChatWindowPositionStyle()}
          className={`${
            windowMode === 'maximized'
              ? 'fixed inset-0 z-50 w-full h-full rounded-none'
              : 'fixed z-50 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800'
          } flex flex-col bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 overflow-hidden font-sans`}
        >
          {/* Header Controls */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white select-none border-b border-slate-800">
            <div className="flex items-center gap-2.5">
              <img
                src="/chatbot-icon.jpg"
                alt="Armellini AI Assistant"
                className="w-9 h-9 rounded-full object-cover border-2 border-sky-400 shadow-sm shrink-0"
              />
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xs font-bold leading-tight">AEL AI Assistant</h3>
                  <span className="text-[9px] px-1.5 py-0.2 rounded bg-sky-950 text-sky-400 font-mono border border-sky-800">
                    Halo AI
                  </span>
                </div>
                {availableModels.length > 0 ? (
                  <select
                    value={selectedModel}
                    onChange={e => setSelectedModel(e.target.value)}
                    className="text-[10px] bg-slate-800 text-sky-300 font-medium rounded px-1.5 py-0.5 border border-slate-700 outline-none hover:border-sky-500 cursor-pointer mt-0.5 max-w-[220px] truncate"
                    title="Active LLM Model (local_llm_config.json)"
                  >
                    {availableModels.map(m => (
                      <option key={m.id} value={m.id} className="bg-slate-900 text-white">
                        {m.name || m.id}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-[10px] text-sky-400 font-medium">Model: {selectedModel}</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setWindowMode('minimized')}
                title="Minimize"
                className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-white cursor-pointer"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setWindowMode(windowMode === 'maximized' ? 'normal' : 'maximized')}
                title={windowMode === 'maximized' ? 'Restore normal size' : 'Maximize full screen'}
                className={`p-1.5 hover:bg-slate-800 rounded cursor-pointer ${
                  windowMode === 'maximized' ? 'text-sky-400 bg-slate-800' : 'text-slate-400 hover:text-white'
                }`}
              >
                {windowMode === 'maximized' ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={handleToggleExpandWindow}
                title={windowMode === 'expanded' ? 'Restore compact chat window' : 'Expand larger chat window'}
                className={`p-1.5 hover:bg-slate-800 rounded cursor-pointer ${
                  windowMode === 'expanded' ? 'text-sky-400 bg-slate-800' : 'text-slate-400 hover:text-white'
                }`}
              >
                {windowMode === 'expanded' ? <Minimize2 className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                title="Close chat (unlocks movable icon)"
                className="p-1.5 hover:bg-red-500/20 rounded text-slate-400 hover:text-red-400 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Tabs Selector (matching Armellini AI Hub) */}
          <div className="flex border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 px-4 shrink-0">
            <button
              onClick={() => setActiveTab('chat')}
              className={`py-2.5 px-3 text-xs font-black cursor-pointer border-b-2 transition flex items-center gap-1.5 ${
                activeTab === 'chat'
                  ? 'border-sky-600 text-sky-600 dark:text-sky-400 font-extrabold'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              AI CRM Copilot
            </button>
            <button
              onClick={() => setActiveTab('advisor')}
              className={`py-2.5 px-3 text-xs font-black cursor-pointer border-b-2 transition flex items-center gap-1.5 ${
                activeTab === 'advisor'
                  ? 'border-sky-600 text-sky-600 dark:text-sky-400 font-extrabold'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Database className="w-3.5 h-3.5" />
              Pipeline Advisor
            </button>
          </div>

          {/* Corner & Edge Resize Handles */}
          {windowMode !== 'maximized' && (
            <>
              {/* Top-Left Corner Handle */}
              <div
                onMouseDown={startResize('nw')}
                className="absolute top-0 left-0 w-4 h-4 cursor-nwse-resize z-50 flex items-start justify-start p-0.5 opacity-60 hover:opacity-100"
                title="Drag top-left corner to resize"
              >
                <div className="w-2.5 h-2.5 border-t-2 border-l-2 border-sky-400 rounded-tl-xs" />
              </div>

              {/* Top-Right Corner Handle */}
              <div
                onMouseDown={startResize('ne')}
                className="absolute top-0 right-0 w-4 h-4 cursor-nesw-resize z-50 flex items-start justify-end p-0.5 opacity-60 hover:opacity-100"
                title="Drag top-right corner to resize"
              >
                <div className="w-2.5 h-2.5 border-t-2 border-r-2 border-sky-400 rounded-tr-xs" />
              </div>

              {/* Bottom-Left Corner Handle */}
              <div
                onMouseDown={startResize('sw')}
                className="absolute bottom-0 left-0 w-4 h-4 cursor-nesw-resize z-50 flex items-end justify-start p-0.5 opacity-70 hover:opacity-100"
                title="Drag bottom-left corner to resize"
              >
                <div className="w-2.5 h-2.5 border-b-2 border-l-2 border-sky-500 rounded-bl-xs" />
              </div>

              {/* Bottom-Right Corner Handle */}
              <div
                onMouseDown={startResize('se')}
                className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-50 flex items-end justify-end p-0.5 opacity-70 hover:opacity-100"
                title="Drag bottom-right corner to resize"
              >
                <div className="w-2.5 h-2.5 border-b-2 border-r-2 border-sky-500 rounded-br-xs" />
              </div>

              {/* Left & Right Edge Resizers */}
              <div
                onMouseDown={startResize('w')}
                className="absolute top-4 bottom-4 left-0 w-1.5 cursor-ew-resize z-40 hover:bg-sky-500/30 transition-colors"
                title="Drag left edge to resize width"
              />
              <div
                onMouseDown={startResize('e')}
                className="absolute top-4 bottom-4 right-0 w-1.5 cursor-ew-resize z-40 hover:bg-sky-500/30 transition-colors"
                title="Drag right edge to resize width"
              />
              <div
                onMouseDown={startResize('n')}
                className="absolute top-0 left-4 right-4 h-1.5 cursor-ns-resize z-40 hover:bg-sky-500/30 transition-colors"
                title="Drag top edge to resize height"
              />
            </>
          )}

          {/* Tab 1: AI CRM Copilot */}
          {activeTab === 'chat' && (
            <div className="flex-1 flex flex-col min-h-0 bg-slate-50 dark:bg-slate-900/30">
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map(m => (
                  <div key={m.id} className={`flex flex-col ${m.sender === 'user' ? 'items-end' : 'items-start'} space-y-1`}>
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                      {m.sender === 'user' ? (
                        <>
                          <span>You</span>
                          <User className="w-3 h-3 text-indigo-500" />
                        </>
                      ) : (
                        <>
                          <img
                            src="/chatbot-icon.jpg"
                            alt="AEL Assistant"
                            className="w-4 h-4 rounded-full object-cover border border-sky-400"
                          />
                          <span>AEL Assistant</span>
                        </>
                      )}
                    </div>

                    <div
                      className={`${
                        m.sender === 'assistant' && (windowMode !== 'normal' || hasMarkdownTable(m.text))
                          ? 'w-full max-w-[96%]'
                          : 'max-w-[88%]'
                      } p-3.5 rounded-2xl text-xs leading-relaxed ${
                        m.sender === 'user'
                          ? 'bg-sky-600 text-white rounded-tr-xs shadow'
                          : 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 rounded-tl-xs border border-slate-200 dark:border-slate-800 shadow-sm'
                      }`}
                    >
                      {/* Tool Execution Badge */}
                      {m.toolsCalled && m.toolsCalled.length > 0 && (
                        <div className="mb-2 inline-flex items-center gap-1 px-2 py-0.5 bg-sky-50 dark:bg-sky-950/60 border border-sky-200 dark:border-sky-800 rounded text-[10px] font-mono text-sky-700 dark:text-sky-300">
                          <Database className="w-3 h-3" />
                          <span>Queried: {m.toolsCalled.join(', ')}</span>
                        </div>
                      )}

                      {m.sender === 'assistant' ? (
                        <div
                          className="prose prose-xs dark:prose-invert max-w-none overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_th]:bg-slate-100 dark:[&_th]:bg-slate-800 [&_th]:p-2 [&_td]:p-2 [&_th]:border [&_td]:border [&_th]:border-slate-300 dark:[&_th]:border-slate-700 [&_td]:border-slate-300 dark:[&_td]:border-slate-700 [&_table]:text-xs"
                          dangerouslySetInnerHTML={renderMarkdown(m.text)}
                        />
                      ) : (
                        <p className="whitespace-pre-wrap">{m.text}</p>
                      )}

                      {/* Export Toolbar */}
                      {m.sender === 'assistant' && (
                        <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2 flex-wrap text-xs text-slate-500">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-400">
                              {m.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {m.model && (
                              <span className="text-[9px] px-1.5 py-0.2 rounded bg-slate-100 dark:bg-slate-800 text-sky-600 dark:text-sky-400 font-mono">
                                ⚡ {m.model}
                              </span>
                            )}
                          </div>

                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setActiveExportMenu(activeExportMenu === m.id ? null : m.id)}
                              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-sky-50 dark:hover:bg-sky-950/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:text-sky-600 dark:hover:text-sky-400 font-semibold cursor-pointer transition"
                              title="Export result as Markdown, PDF, or CSV"
                            >
                              <Download className="w-3.5 h-3.5" />
                              <span>Export</span>
                            </button>

                            {activeExportMenu === m.id && (
                              <div className="absolute right-0 bottom-9 z-40 w-44 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl py-1.5 text-xs">
                                <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-800 mb-1">
                                  Choose Format
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    exportToCSV(m.text);
                                    setActiveExportMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer font-medium text-slate-700 dark:text-slate-200"
                                >
                                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                  <span>CSV {hasMarkdownTable(m.text) ? '(Table)' : '(Data)'}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    exportToPDF(m.text);
                                    setActiveExportMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer font-medium text-slate-700 dark:text-slate-200"
                                >
                                  <FileType className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                  <span>PDF (Report)</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    exportToMarkdown(m.text);
                                    setActiveExportMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer font-medium text-slate-700 dark:text-slate-200"
                                >
                                  <FileText className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                                  <span>Markdown (.md)</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 text-sky-600 animate-spin" />
                      <span className="text-xs text-slate-500 font-medium animate-pulse">AI Agent searching database...</span>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Suggested Prompts (matching Armellini AI Hub) */}
              {messages.length === 1 && (
                <div className="px-4 py-2 flex flex-wrap gap-1.5 justify-center border-t border-slate-200/60 dark:border-slate-800/60 bg-white/60 dark:bg-slate-950/60">
                  {SUGGESTED_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => sendMessage(p)}
                      className="text-[10px] bg-slate-100 hover:bg-sky-50 dark:bg-slate-800 dark:hover:bg-sky-950/40 text-slate-600 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400 px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-700 cursor-pointer transition font-medium"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}

              {/* Input Form */}
              <div className="p-3 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800">
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    sendMessage();
                  }}
                  className="flex items-center gap-2"
                >
                  <input
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder="Search database, clients, shippers or email summaries..."
                    className="flex-1 px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 font-medium"
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || loading}
                    className="p-2.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white rounded-xl cursor-pointer transition"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Tab 2: Pipeline Advisor (matching Armellini AI Hub) */}
          {activeTab === 'advisor' && (
            <div className="flex-1 flex flex-col min-h-0 bg-slate-50 dark:bg-slate-900/30">
              <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search leads by name or company..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 text-slate-800 dark:text-slate-200"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {candidates.length > 0 ? (
                  candidates.map(lead => {
                    const recommendation = recommendations[lead.id];
                    const isLoading = loadingLeadId === lead.id;
                    const isApplied = appliedLeads[lead.id];

                    return (
                      <div
                        key={lead.id}
                        className="border border-slate-200 dark:border-slate-800 rounded-xl p-3.5 bg-white dark:bg-slate-950 flex flex-col gap-3 transition hover:shadow-md"
                      >
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-slate-800 dark:text-slate-100 text-xs">{lead.name}</span>
                            <span className="text-[11px] text-slate-400 font-medium">({lead.company})</span>
                            <span
                              className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded font-mono"
                              style={{
                                backgroundColor: `${getStatusBgColor(lead.status)}20`,
                                color: getStatusBgColor(lead.status),
                                border: `1px solid ${getStatusBgColor(lead.status)}40`
                              }}
                            >
                              {lead.status}
                            </span>
                          </div>

                          <div className="bg-slate-50 dark:bg-slate-900/60 rounded-lg p-2 text-[11px] text-slate-600 dark:text-slate-300 border border-slate-100 dark:border-slate-800">
                            <span className="font-semibold text-slate-400 block mb-0.5 text-[9px] uppercase tracking-wide">
                              Next Steps / Activity Log
                            </span>
                            <p className="italic">{lead.nextSteps || 'No next steps populated.'}</p>
                          </div>
                        </div>

                        <div className="flex flex-col justify-center items-end gap-2 border-t border-slate-100 dark:border-slate-800 pt-2.5">
                          {!recommendation ? (
                            <button
                              onClick={() => handleGetRecommendation(lead)}
                              disabled={isLoading || !lead.nextSteps}
                              className={`w-full py-1.5 px-3 text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 cursor-pointer transition ${
                                isLoading
                                  ? 'bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400 animate-pulse'
                                  : !lead.nextSteps
                                  ? 'bg-slate-100 dark:bg-slate-800/50 text-slate-400 cursor-not-allowed'
                                  : 'bg-sky-600 hover:bg-sky-700 text-white shadow-sm'
                              }`}
                            >
                              {isLoading ? (
                                <>
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  <span>Analyzing Lead...</span>
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-3.5 h-3.5" />
                                  <span>Get AI Advice</span>
                                </>
                              )}
                            </button>
                          ) : (
                            <div className="w-full space-y-2 text-left">
                              <div className="flex items-start gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                                <AlertCircle className="w-3.5 h-3.5 text-sky-500 shrink-0 mt-0.5" />
                                <p className="leading-tight">
                                  <span className="font-bold text-slate-700 dark:text-slate-200">Advice:</span>{' '}
                                  {recommendation.reasoning}
                                </p>
                              </div>

                              <div className="flex items-center gap-2">
                                {recommendation.recommendedStatus === lead.status ? (
                                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-50 dark:bg-emerald-950/30 px-2 py-1 rounded w-full text-center border border-emerald-200/50">
                                    ✓ Current status is optimal
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => handleApplyStatus(lead.id, recommendation.recommendedStatus)}
                                    disabled={isApplied || !onUpdateStatus}
                                    className={`w-full py-1.5 px-3 text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 cursor-pointer transition ${
                                      isApplied
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-300 border border-emerald-200'
                                    }`}
                                  >
                                    {isApplied ? (
                                      <>
                                        <Check className="w-3.5 h-3.5" />
                                        <span>Applied!</span>
                                      </>
                                    ) : (
                                      <>
                                        <span>Change to </span>
                                        <span
                                          className="px-1.5 py-0.5 rounded text-[10px] font-extrabold uppercase ml-1"
                                          style={{
                                            backgroundColor: getStatusBgColor(recommendation.recommendedStatus),
                                            color: '#fff'
                                          }}
                                        >
                                          {recommendation.recommendedStatus === 'Revisit Later | ADD FU DATE'
                                            ? 'Revisit Later'
                                            : recommendation.recommendedStatus}
                                        </span>
                                      </>
                                    )}
                                  </button>
                                )}

                                <button
                                  onClick={() => {
                                    setRecommendations(prev => {
                                      const next = { ...prev };
                                      delete next[lead.id];
                                      return next;
                                    });
                                  }}
                                  className="p-1.5 border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-600 transition cursor-pointer"
                                  title="Clear suggestion"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="py-12 text-center text-xs text-slate-400 dark:text-slate-500">
                    No matching leads found.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bottom Resize & Window Size Control Bar */}
          {windowMode !== 'maximized' && (
            <div className="px-3 py-1.5 bg-slate-100 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 select-none shrink-0">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => adjustWindowSize(-100, -60)}
                  className="px-2 py-0.5 rounded bg-white dark:bg-slate-800 hover:bg-sky-50 dark:hover:bg-sky-950/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold cursor-pointer transition"
                  title="Make chat window smaller"
                >
                  − Smaller
                </button>
                <button
                  type="button"
                  onClick={resetWindowSize}
                  className="px-2 py-0.5 rounded bg-white dark:bg-slate-800 hover:bg-sky-50 dark:hover:bg-sky-950/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-mono cursor-pointer transition"
                  title="Reset to default 900px width"
                >
                  900px Default
                </button>
                <button
                  type="button"
                  onClick={() => adjustWindowSize(100, 60)}
                  className="px-2 py-0.5 rounded bg-white dark:bg-slate-800 hover:bg-sky-50 dark:hover:bg-sky-950/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold cursor-pointer transition"
                  title="Make chat window larger"
                >
                  + Larger
                </button>
              </div>

              {/* Bottom Center Drag Grip */}
              <div
                onMouseDown={startResize('s')}
                className="flex items-center gap-1.5 px-3 py-1 cursor-ns-resize hover:text-sky-500 transition-colors"
                title="Drag bottom edge to resize height"
              >
                <div className="w-8 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
                <span className="font-mono text-[9px] hidden sm:inline">
                  {dimensions.width}×{dimensions.height}px
                </span>
              </div>

              {/* Bottom Corner Drag Grip Indicator */}
              <div
                onMouseDown={startResize('se')}
                className="flex items-center gap-1 px-1.5 py-0.5 cursor-nwse-resize hover:text-sky-500 transition-colors"
                title="Drag corner to resize window"
              >
                <Move className="w-3 h-3" />
                <span className="text-[9px] hidden md:inline">Resize Corner</span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default LogisticsChatWidget;
